import { hasLowerLevel, isMapId, levelAt, mapInfo, parseGetpos, radarToWorld, siteAt, siteBox, teleportCommand, type Level, type MapId, type RadarPoint } from "@69ly/maps";
import {
  BOMB_SECONDS,
  MAX_PLANT_PICTURES,
  PLANT_CAPTIONS,
  PlantSpotInput,
  SITES,
  type Landing,
  type PlantCaption,
  type PlantSpot,
  type Scope,
  type Site,
  type Stuff,
} from "@69ly/shared";
import { useRef, useState, type FormEvent, type PointerEvent as ReactPointerEvent } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { ClockRange } from "../components/ClockRange.tsx";
import { GrenadeGlyph } from "../components/GrenadeGlyph.tsx";
import { aimPoint, PlantOnMap, SiteOutline } from "../components/PlantBits.tsx";
import { focusOn, OnMap, Radar } from "../components/Radar.tsx";
import { DropZone, IMAGE_ACCEPT } from "../components/DropZone.tsx";
import { Banner, ErrorState, Loading } from "../components/Status.tsx";
import { move, RowMoves, StuffPicker } from "../components/StuffPicker.tsx";
import { PERSONAL, useCurrentTeam } from "../lib/current-team.tsx";
import { errorText } from "../lib/errors.ts";
import { useLeave } from "../lib/leave.ts";
import { useMe, useTeam } from "../lib/queries.ts";
import { useStuffs } from "../lib/stuff-queries.ts";
import { uploadPlantPicture, usePlantSpot, useRemovePlantPicture, useSavePlantSpot } from "../lib/teamplay-queries.ts";
import { Segmented } from "./StuffForm.tsx";

interface UtilityDraft {
  key: string;
  stuff: Stuff;
  slot: number;
  /** Bomb timer, counting down. */
  from: number;
  to: number;
}

interface PositionDraft {
  key: string;
  slot: number;
  label: string;
  x: number | null;
  y: number | null;
  z: number | null;
  yaw: string;
  /** How far they look, world units; null = the default length. */
  reach: number | null;
  memberId: string | null;
}

/**
 * The dot at the end of a placed player's view line: drag it to where they look.
 * It reads the pointer on the radar's own layer, so zoom and pan do not matter.
 */
function AimHandle({ map, p, onAim, label }: { map: MapId; p: PositionDraft & { x: number; y: number }; onAim: (yaw: number, reach: number) => void; label: string }) {
  const layer = useRef<HTMLElement | null>(null);
  const end = aimPoint({ x: p.x, y: p.y, yaw: Number(p.yaw) || 0, reach: p.reach });
  const move = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (!layer.current || !e.currentTarget.hasPointerCapture(e.pointerId)) return;
    const r = layer.current.getBoundingClientRect();
    const w = radarToWorld(map, { u: (e.clientX - r.left) / r.width, v: (e.clientY - r.top) / r.height });
    const reach = Math.hypot(w.x - p.x, w.y - p.y);
    if (reach < 16) return;
    onAim(Math.round((Math.atan2(w.y - p.y, w.x - p.x) * 180) / Math.PI), Math.round(reach));
  };
  return (
    <OnMap map={map} x={end.x} y={end.y}>
      <button
        type="button"
        className={`pp-aim slot-${p.slot}`}
        aria-label={label}
        title={label}
        onPointerDown={(e) => {
          e.stopPropagation();
          layer.current = e.currentTarget.closest<HTMLElement>(".radar-layer");
          e.currentTarget.setPointerCapture(e.pointerId);
        }}
        onPointerMove={move}
        onPointerUp={(e) => e.currentTarget.releasePointerCapture(e.pointerId)}
        onClick={(e) => e.stopPropagation()}
      />
    </OnMap>
  );
}

/** Bomb timer seconds as 0:35. */
const bombClock = (s: number) => `0:${String(s).padStart(2, "0")}`;

let nextKey = 0;
const key = () => `p${nextKey++}`;
const SLOTS = [1, 2, 3, 4, 5];

function PlantSpotForm({ map, scope, plant, singles }: { map: MapId; scope: Scope; plant?: PlantSpot; singles: Stuff[] }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const leave = useLeave();
  const [params] = useSearchParams();
  const save = useSavePlantSpot(plant?.id);
  const removePicture = useRemovePlantPicture(plant?.id ?? "");
  const info = mapInfo(map);

  const [name, setName] = useState(plant?.name ?? "Default");
  // Post-plants only: retakes are built with the executions.
  const kind = "postplant" as const;
  // Who can be pinned: the team's players, or only me in a personal library.
  const team = useTeam(scope === PERSONAL ? undefined : scope);
  const me = useMe().data?.user;
  const people = scope === PERSONAL ? (me ? [{ id: me.id, name: me.name }] : []) : (team.data?.members.map((m) => ({ id: m.userId, name: m.name })) ?? []);
  const [site, setSite] = useState<Site>(plant?.site ?? (params.get("site") === "b" ? "b" : "a"));
  const [spot, setSpot] = useState<Landing | null>(plant?.plant ?? null);
  const [spotText, setSpotText] = useState("");
  const [notes, setNotes] = useState(plant?.notes ?? "");
  const [utility, setUtility] = useState<UtilityDraft[]>(
    plant?.utility.map((u) => ({ key: key(), stuff: u.stuff, slot: u.slot, from: u.from, to: u.to })) ?? [],
  );
  const [positions, setPositions] = useState<PositionDraft[]>(
    plant?.positions.map((p) => ({ key: key(), slot: p.slot, label: p.label, x: p.x, y: p.y, z: p.z, yaw: String(Math.round(p.yaw)), reach: p.reach, memberId: p.memberId })) ?? [],
  );
  const [picking, setPicking] = useState<"plant" | string>("plant");
  const [level, setLevel] = useState<Level>(plant ? levelAt(map, plant.plant.z ?? 0) : (siteBox(map, site)?.level ?? "upper"));
  const [pictures, setPictures] = useState<{ file: File; caption: PlantCaption }[]>([]);
  const [pickNote, setPickNote] = useState<string | null>(null);
  const location = useLocation();
  const [error, setError] = useState<string | null>(null);
  // Upload errors survive the switch from the new plant spot form to the edit form.
  const [uploadErrors, setUploadErrors] = useState<string[]>(() => (location.state as { uploadErrors?: string[] } | null)?.uploadErrors ?? []);
  const existing = plant?.media ?? [];
  // Pictures fill the stand, aim and planted slots first.
  const addPictures = (picked: File[]) => {
    const files = picked.slice(0, pictureRoom);
    const used = new Set<PlantCaption>([...existing.map((m) => m.caption), ...pictures.map((p) => p.caption)]);
    setPictures([
      ...pictures,
      ...files.map((file) => {
        const caption: PlantCaption = (["stand", "aim", "planted"] as const).find((c) => !used.has(c)) ?? "other";
        used.add(caption);
        return { file, caption };
      }),
    ]);
  };
  const pictureRoom = MAX_PLANT_PICTURES - existing.length - pictures.length;
  const [busy, setBusy] = useState(false);

  const setPos = (i: number, change: Partial<PositionDraft>) => setPositions(positions.map((p, j) => (j === i ? { ...p, ...change } : p)));
  const setUtil = (i: number, change: Partial<UtilityDraft>) => setUtility(utility.map((u, j) => (j === i ? { ...u, ...change } : u)));

  const pick = (p: RadarPoint) => {
    const w = radarToWorld(map, p);
    const z = level === "lower" ? (mapInfo(map).lowerBelowZ ?? 0) - 100 : null;
    if (picking === "plant") {
      // The bomb goes down on a site only. A click on the other site moves the plant spot there.
      const on = siteAt(map, w.x, w.y, z);
      if (!on) return setPickNote(t("plantForm.outsideSite", { site: t(`area.${site}`) }));
      if (on !== site) setSite(on);
      setPickNote(null);
      setSpot({ x: w.x, y: w.y, z });
    } else {
      const i = positions.findIndex((x) => x.key === picking);
      if (i >= 0) setPos(i, { x: w.x, y: w.y, z });
    }
  };

  // What the preview map draws: the spot, the utility, the players placed so far.
  const preview: PlantSpot | null = spot
    ? {
        id: "preview",
        teamId: null,
        map,
        kind,
        site,
        name,
        plant: spot,
        notes: "",
        media: [],
        utility: utility.map((u) => ({ slot: u.slot, from: u.from, to: u.to, stuff: u.stuff })),
        positions: positions
          .filter((p) => p.x !== null && p.y !== null)
          .map((p) => ({ slot: p.slot, label: p.label, x: p.x!, y: p.y!, z: p.z, yaw: Number(p.yaw) || 0, reach: p.reach, memberId: p.memberId, member: null })),
        author: { id: "", name: "" },
        canEdit: true,
        createdAt: "",
        updatedAt: "",
      }
    : null;
  // Zoomed on the site, wide enough for the players and the utility around it.
  const box = siteBox(map, site);
  const boxCorners = box
    ? [radarToWorld(map, { u: box.u0, v: box.v0 }), radarToWorld(map, { u: box.u1, v: box.v1 })]
    : [];
  const focus = focusOn(map, [...boxCorners, ...(spot ? [spot] : []), ...(preview?.positions ?? []), ...utility.map((u) => u.stuff.landing)], {
    maxScale: 3,
    pad: 0.08,
  });
  const spotOutside = spot && !siteAt(map, spot.x, spot.y, spot.z);
  const changeSite = (s: Site) => {
    setSite(s);
    setPickNote(null);
    if (spot && siteAt(map, spot.x, spot.y, spot.z) !== s) setSpot(null);
    setLevel(siteBox(map, s)?.level ?? "upper");
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!spot) return setError(t("plantForm.spotNone"));
    if (positions.some((p) => p.x === null)) return setError(t("plantForm.positionNone"));
    const input = PlantSpotInput.safeParse({
      map,
      kind,
      site,
      name,
      plant: spot,
      notes,
      utility: utility.map((u) => ({ stuffId: u.stuff.id, slot: u.slot, from: u.from, to: u.to })),
      positions: positions.map((p) => ({ slot: p.slot, label: p.label, x: p.x, y: p.y, z: p.z, yaw: Number(p.yaw) || 0, reach: p.reach, memberId: p.memberId })),
    });
    if (!input.success) return setError(input.error.issues[0]?.message ?? t("errors.invalid_input"));
    setBusy(true);
    try {
      const saved = await save.mutateAsync(plant ? input.data : { ...input.data, scope });
      const failed: string[] = [];
      for (const p of pictures) {
        try {
          await uploadPlantPicture(saved.id, p.file, p.caption);
        } catch (err) {
          failed.push(t("form.uploadFailed", { name: p.file.name, reason: errorText(t, err) }));
        }
      }
      if (failed.length) {
        setBusy(false);
        setPictures([]);
        setUploadErrors(failed);
        return navigate(`/plant-spots/${saved.id}/edit`, { replace: true, state: { uploadErrors: failed } });
      }
      if (plant) leave.back(`/library/${map}/post-plant?spot=${saved.id}`);
      else leave.to(`/library/${map}/post-plant?spot=${saved.id}`);
    } catch (err) {
      setError(errorText(t, err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="page stuff-form" onSubmit={submit} noValidate>
      <div className="page-head">
        <h1 className="page-title">
          {plant ? t("plantForm.editTitle", { name: plant.name }) : t("plantForm.newTitle", { map: info.name })}
        </h1>
        <div className="row">
          <button type="button" className="btn btn-quiet" onClick={() => leave.back(`/library/${map}/post-plant${plant ? `?spot=${plant.id}` : ""}`)}>
            {t("form.cancel")}
          </button>
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? t("form.saving") : t("plantForm.save")}
          </button>
        </div>
      </div>
      {error && <Banner tone="warning">{error}</Banner>}
      {uploadErrors.map((m) => (
        <Banner key={m} tone="warning">
          {m}
        </Banner>
      ))}

      <div className="columns">
        <div className="column-main form-main">
          <section className="card">
            <h2 className="section-title">1 · {t("plantForm.stepSpot")}</h2>
            <div className="field-row">
              <label className="field">
                <span>{t("plantForm.name")}</span>
                <input value={name} onChange={(e) => setName(e.target.value)} maxLength={30} placeholder="Default" />
              </label>
              <Segmented label={t("plant.site")} options={SITES} value={site} onChange={changeSite} render={(s) => t(`areaShort.${s}`)} />
            </div>
            <p className="muted small">{t("plantForm.postplantHelp")}</p>
            <p className="muted small">{t("plantForm.mapHelp")}</p>
            <div className="segmented" role="group" aria-label={t("plantForm.clickPlaces")}>
              <button type="button" className="seg" aria-pressed={picking === "plant"} onClick={() => setPicking("plant")}>
                {t("plantForm.clickPlant")}
              </button>
              {positions.map((p) => (
                <button key={p.key} type="button" className={`seg slot-${p.slot}`} aria-pressed={picking === p.key} onClick={() => setPicking(p.key)}>
                  <span className="slot-dot" /> {p.label || t("plant.playerN", { n: p.slot })}
                </button>
              ))}
            </div>
            {hasLowerLevel(map) && (
              <div className="segmented" role="group" aria-label={t("level.label")}>
                {(["upper", "lower"] as const).map((l) => (
                  <button key={l} type="button" className="seg" aria-pressed={level === l} onClick={() => setLevel(l)}>
                    {t(`level.${l}`)}
                  </button>
                ))}
              </div>
            )}
            <div className="pick-radar">
              <Radar map={map} mapName={info.name} level={level} onPick={pick} focus={focus}>
                {SITES.map((s) => (
                  <SiteOutline key={s} map={map} site={s} level={level} active={s === site} />
                ))}
                {preview && <PlantOnMap map={map} plant={preview} level={level} layers={{ utility: true, positions: true, cones: true }} />}
                {positions.map((p, i) =>
                  p.x !== null && p.y !== null && levelAt(map, p.z ?? spot?.z ?? 0) === level ? (
                    <AimHandle
                      key={p.key}
                      map={map}
                      p={{ ...p, x: p.x, y: p.y }}
                      label={t("plantForm.aimHandle", { name: p.label || t("plant.playerN", { n: p.slot }) })}
                      onAim={(yaw, reach) => setPos(i, { yaw: String(yaw), reach })}
                    />
                  ) : null,
                )}
                {spot && (
                  <OnMap map={map} x={spot.x} y={spot.y}>
                    <span className="c4-marker is-static" tabIndex={0} title={name}>
                      <span className="c4-tag">C4</span>
                      <span className="c4-name">{name}</span>
                    </span>
                  </OnMap>
                )}
              </Radar>
            </div>
            <label className="field">
              <span>{t("plantForm.spotGetpos")}</span>
              <input
                className="mono"
                value={spotText}
                spellCheck={false}
                placeholder="setpos -300 -2100 -170"
                onChange={(e) => {
                  setSpotText(e.target.value);
                  const p = parseGetpos(e.target.value);
                  if (p) {
                    // getpos comes from the game: trust it, even off the outlines we know.
                    const on = siteAt(map, p.x, p.y, p.z);
                    if (on && on !== site) setSite(on);
                    setPickNote(null);
                    setSpot({ x: p.x, y: p.y, z: p.z });
                    setLevel(levelAt(map, p.z));
                  }
                }}
              />
            </label>
            {pickNote && (
              <p className="field-error small" role="alert">
                {pickNote}
              </p>
            )}
            {spot && <p className="ok-text small">{t("plantForm.spotSet", { x: Math.round(spot.x), y: Math.round(spot.y) })}</p>}
            {spotOutside && <p className="muted small">{t("plantForm.spotOffOutline")}</p>}
          </section>

          <section className="card">
            <h2 className="section-title">2 · {t("plantForm.stepUtility")}</h2>
            <p className="muted">{t("plantForm.stepUtilityHelp", { seconds: BOMB_SECONDS })}</p>
            {utility.length > 0 && (
              <ol className="draft-steps">
                {utility.map((u, i) => (
                  <li key={u.key} className={`draft-step slot-${u.slot}`}>
                    <GrenadeGlyph grenade={u.stuff.grenade} size={14} />
                    <span className="draft-step-title">{u.stuff.title}</span>
                    <select aria-label={t("executionForm.playerFor", { title: u.stuff.title })} value={u.slot} onChange={(e) => setUtil(i, { slot: Number(e.target.value) })}>
                      {SLOTS.map((n) => (
                        <option key={n} value={n}>
                          {t("plant.playerN", { n })}
                        </option>
                      ))}
                    </select>
                    <div className="draft-step-range">
                      <ClockRange
                        max={BOMB_SECONDS}
                        from={u.from}
                        to={u.to}
                        format={bombClock}
                        labelFrom={t("plantForm.timerFrom", { title: u.stuff.title })}
                        labelTo={t("plantForm.timerTo", { title: u.stuff.title })}
                        onChange={(from, to) => setUtil(i, { from, to })}
                      />
                    </div>
                    <RowMoves index={i} count={utility.length} label={u.stuff.title} onMove={(a, b) => setUtility(move(utility, a, b))} onRemove={() => setUtility(utility.filter((_, j) => j !== i))} />
                  </li>
                ))}
              </ol>
            )}
            <StuffPicker
              stuffs={singles}
              side="t"
              disabled={utility.length >= 10}
              onPick={(p) => p.kind === "single" && setUtility([...utility, { key: key(), stuff: p.stuff, slot: 1, from: 35, to: 25 }])}
            />
          </section>

          <section className="card">
            <h2 className="section-title">3 · {t("plantForm.stepPositions")}</h2>
            <p className="muted">{t("plantForm.stepPositionsHelp")}</p>
            {positions.map((p, i) => (
              <div key={p.key} className={`draft-step slot-${p.slot} position-row`}>
                <select aria-label={t("plantForm.whichPlayer")} value={p.slot} onChange={(e) => setPos(i, { slot: Number(e.target.value) })}>
                  {SLOTS.map((n) => (
                    <option key={n} value={n}>
                      {t("plant.playerN", { n })}
                    </option>
                  ))}
                </select>
                <input className="draft-step-note" value={p.label} maxLength={40} placeholder={t("plantForm.labelPlaceholder")} aria-label={t("plantForm.label")} onChange={(e) => setPos(i, { label: e.target.value })} />
                <input
                  className="draft-step-note mono"
                  placeholder={t("plantForm.positionGetpos")}
                  aria-label={t("plantForm.positionGetpos")}
                  spellCheck={false}
                  onChange={(e) => {
                    const g = parseGetpos(e.target.value);
                    if (g) setPos(i, { x: g.x, y: g.y, z: g.z, yaw: g.yaw !== undefined ? String(Math.round(g.yaw)) : p.yaw });
                  }}
                />
                <label className="field-inline small">
                  {t("plantForm.yaw")}
                  <input className="draft-input mono" style={{ width: 60 }} inputMode="numeric" value={p.yaw} onChange={(e) => setPos(i, { yaw: e.target.value })} />
                </label>
                <select aria-label={t("plantForm.pinFor", { name: p.label || t("plant.playerN", { n: p.slot }) })} value={p.memberId ?? ""} onChange={(e) => setPos(i, { memberId: e.target.value || null })}>
                  <option value="">{t("plantForm.anyone")}</option>
                  {people.map((m) => (
                    <option key={m.id} value={m.id} disabled={positions.some((x, j) => j !== i && x.memberId === m.id)}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <span className={p.x === null ? "field-error small" : "ok-text small"}>
                  {p.x === null ? t("plantForm.notPlaced") : teleportCommand({ x: p.x, y: p.y ?? 0, z: p.z ?? 0, pitch: 0, yaw: Number(p.yaw) || 0 })}
                </span>
                <RowMoves index={i} count={positions.length} label={p.label || t("plant.playerN", { n: p.slot })} onMove={(a, b) => setPositions(move(positions, a, b))} onRemove={() => setPositions(positions.filter((_, j) => j !== i))} />
              </div>
            ))}
            {positions.length < 5 && (
              <button
                type="button"
                className="btn btn-secondary btn-small"
                onClick={() => {
                  const slot = SLOTS.find((n) => !positions.some((x) => x.slot === n)) ?? 1;
                  const d = { key: key(), slot, label: "", x: null, y: null, z: null, yaw: "0", reach: null, memberId: null };
                  setPositions([...positions, d]);
                  setPicking(d.key);
                }}
              >
                {t("plantForm.addPosition")}
              </button>
            )}
          </section>
        </div>

        <aside className="column-side form-side">
          <section className="card">
            <h2 className="section-title">{t("plant.wherePlant")}</h2>
            <p className="muted small">{t("plantForm.pictureHelp", { count: MAX_PLANT_PICTURES })}</p>
            <ul className="picture-list">
              {existing.map((m) => (
                <li key={m.id}>
                  <img src={m.url} alt="" />
                  <span>{t(`plantCaption.${m.caption}`)}</span>
                  <button type="button" className="btn btn-quiet btn-small" disabled={removePicture.isPending} onClick={() => removePicture.mutate(m.id)}>
                    {t("form.removePicture")}
                  </button>
                </li>
              ))}
              {pictures.map((p, i) => (
                <li key={`${p.file.name}-${i}`}>
                  <span className="file-name">{p.file.name}</span>
                  <label>
                    <span className="visually-hidden">{t("form.caption")}</span>
                    <select value={p.caption} onChange={(e) => setPictures(pictures.map((x, j) => (j === i ? { ...x, caption: e.target.value as PlantCaption } : x)))}>
                      {PLANT_CAPTIONS.map((c) => (
                        <option key={c} value={c}>
                          {t(`plantCaption.${c}`)}
                        </option>
                      ))}
                    </select>
                  </label>
                  <button type="button" className="btn btn-quiet btn-small" onClick={() => setPictures(pictures.filter((_, j) => j !== i))}>
                    {t("form.removePicture")}
                  </button>
                </li>
              ))}
            </ul>
            {pictureRoom > 0 && (
              <DropZone accept={IMAGE_ACCEPT} multiple hint={t("drop.pictures")} onFiles={addPictures} onRefused={(names) => setError(t("drop.refused", { names: names.join(", ") }))}>
                <label className="btn btn-secondary btn-small file-button">
                  {t("form.addPicture")}
                  <input
                    type="file"
                    accept={IMAGE_ACCEPT.join(",")}
                    multiple
                    onChange={(e) => {
                      addPictures(Array.from(e.target.files ?? []));
                      e.target.value = "";
                    }}
                  />
                </label>
              </DropZone>
            )}
          </section>
          <section className="card">
            <label className="field">
              <span>{t("form.notes")}</span>
              <textarea rows={4} value={notes} maxLength={1000} onChange={(e) => setNotes(e.target.value)} />
            </label>
          </section>
        </aside>
      </div>
    </form>
  );
}

export function NewPlantSpotPage() {
  const { t } = useTranslation();
  const { map } = useParams();
  const { scope } = useCurrentTeam();
  const singles = useStuffs(scope, isMapId(map) ? map : "");
  if (!isMapId(map)) return <Navigate to="/library" replace />;
  if (singles.isPending) return <Loading />;
  if (singles.error) return <ErrorState message={errorText(t, singles.error)} onRetry={() => void singles.refetch()} />;
  return <PlantSpotForm map={map} scope={scope} singles={singles.data} />;
}

export function EditPlantSpotPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const plant = usePlantSpot(id);
  const scope: Scope = plant.data ? (plant.data.teamId ?? PERSONAL) : PERSONAL;
  const singles = useStuffs(scope, plant.data?.map ?? "");
  if (plant.isPending || singles.isPending) return <Loading />;
  if (plant.error) return <ErrorState message={errorText(t, plant.error)} onRetry={() => void plant.refetch()} />;
  if (singles.error) return <ErrorState message={errorText(t, singles.error)} onRetry={() => void singles.refetch()} />;
  if (!isMapId(plant.data.map) || !plant.data.canEdit) return <Navigate to={`/library/${plant.data.map}/post-plant?spot=${plant.data.id}`} replace />;
  return <PlantSpotForm map={plant.data.map} scope={scope} plant={plant.data} singles={singles.data} />;
}
